import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PulseCounts } from "../../../types";

interface Props {
  counts: PulseCounts;
}

/**
 * Counters are navigation, not statistics: each one is a way into a filtered
 * list. Nothing here exists only to be read — that was the old dashboard.
 *
 * Links are bare routes, not filtered ones: neither `/orders` nor
 * `/purchase-requests` reads its filter (state/status) from the query
 * string — both keep filter state in local React state that always starts
 * unfiltered. A counter that looks like it filters but silently lands on an
 * unfiltered list is worse than one that is honest about not filtering, so
 * these tiles link to the bare list screens instead of inventing a query
 * parameter the receiving screen would ignore.
 */
const PulseBar = ({ counts }: Props) => {
  const { t } = useTranslation();

  const tiles = [
    {
      key: "rfqsPendingAward",
      value: counts.rfqsPendingAward,
      to: "/orders",
      hot: true,
    },
    { key: "openRfqs", value: counts.openRfqs, to: "/orders", hot: false },
    {
      key: "scheduledRfqs",
      value: counts.scheduledRfqs,
      to: "/orders",
      hot: false,
    },
    {
      key: "requestsInProcess",
      value: counts.requestsInProcess,
      to: "/purchase-requests",
      hot: false,
    },
    {
      key: "pendingDelivery",
      value: counts.pendingDelivery,
      // pendingDelivery is a count of PurchaseRequestV2 rows in PENDING_DELIVERY
      // status, not Delivery entities — /deliveries lists the wrong entity type
      // entirely, so the number and the destination could never match.
      to: "/purchase-requests",
      hot: false,
    },
  ];

  return (
    <div className="manager-dashboard__pulse">
      {tiles.map((tile) => (
        <RouterLink
          key={tile.key}
          to={tile.to}
          className="manager-dashboard__kpi"
        >
          <span
            className={
              tile.hot
                ? "manager-dashboard__kpi-value manager-dashboard__kpi-value--hot"
                : "manager-dashboard__kpi-value"
            }
          >
            {tile.value}
          </span>
          <span className="manager-dashboard__kpi-label">
            {t(`managerDashboard.pulse.${tile.key}`)}
          </span>
        </RouterLink>
      ))}
    </div>
  );
};

export default PulseBar;
